import React from 'react';
import { Typography, type SxProps, type Theme, type TypographyProps } from '@mui/material';
import { keyframes } from '@mui/material/styles';

const shimmerSweep = keyframes`
  0% { background-position: -156px 0; }
  100% { background-position: calc(100% + 156px) 0; }
`;

type AnimatedShimmerTextProps = {
  text: string;
  noWrap?: boolean;
  sx?: SxProps<Theme>;
  variant?: TypographyProps['variant'];
};

const AnimatedShimmerText: React.FC<AnimatedShimmerTextProps> = ({
  text,
  noWrap = false,
  sx,
  variant,
}) => {
  const callerSx = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <Typography
      component="span"
      variant={variant}
      role="status"
      aria-label={text}
      data-shimmer-text={text}
      noWrap={noWrap}
      sx={[
        {
          position: 'relative',
          display: 'block',
          color: 'rgba(255,255,255,0.36)',
          WebkitTextFillColor: 'rgba(255,255,255,0.36)',
          '&::after': {
            content: 'attr(data-shimmer-text)',
            position: 'absolute',
            inset: 0,
            boxSizing: 'border-box',
            padding: 'inherit',
            overflow: 'hidden',
            whiteSpace: noWrap ? 'nowrap' : 'inherit',
            textOverflow: noWrap ? 'ellipsis' : 'inherit',
            backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.52), #fff, rgba(255,255,255,0.52), rgba(255,255,255,0))',
            backgroundSize: '156px 100%',
            backgroundRepeat: 'no-repeat',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            WebkitTextFillColor: 'transparent',
            animation: `${shimmerSweep} 2.4s ease-in-out infinite`,
            pointerEvents: 'none',
          },
          '@media (prefers-reduced-motion: reduce)': {
            color: 'rgba(255,255,255,0.68)',
            WebkitTextFillColor: 'rgba(255,255,255,0.68)',
            '&::after': { display: 'none' },
          },
        },
        ...callerSx,
      ]}
    >
      {text}
    </Typography>
  );
};

export default AnimatedShimmerText;
